import {
  createColumnConfig,
  StandardTableConfigWithNoGroups,
} from "@stenajs-webui/grid";
import { PriceListRow } from "../types/PriceListRow";
import { formatUnaccompanied } from "./priceListValues";

/**
 * Every row field, plus the currency prices were converted into. That one is
 * not on the row - it is what the search asked for - but it gets a column of
 * its own so that every row still says which currency its price is in once
 * it is copied out of the sheet.
 */
export type PriceListExcelColumnKey =
  | Exclude<keyof PriceListRow, "__typename">
  | "conversionCurrency";

export const createPriceListExcelConfig = (
  resolveVehicleTypeName: (code: string) => string,
  conversionCurrency: string | undefined
): StandardTableConfigWithNoGroups<PriceListRow, PriceListExcelColumnKey> => ({
  keyResolver: (row) =>
    [
      row.routeAgreementNumber,
      row.route,
      row.vehicleType,
      row.length,
      row.weightFrom,
    ].join("-"),
  columnOrder: [
    "routeAgreementNumber",
    "customerIndex",
    "validFrom",
    "validTo",
    "route",
    "sailingCategory",
    "routeAgreementName",
    "vehicleType",
    "length",
    "weightFrom",
    "weightTo",
    "unaccompanied",
    "price",
    "routeAgreementCurrency",
    "routeAgreementVehicleType",
    "customerNumber",
    "movementType",
    "convertedPrice",
    "conversionCurrency",
  ],
  columns: {
    routeAgreementNumber: createColumnConfig(
      (row) => row.routeAgreementNumber,
      { columnLabel: "Agreement number" }
    ),
    customerIndex: createColumnConfig((row) => row.customerIndex, {
      columnLabel: "Customer",
    }),
    validFrom: createColumnConfig((row) => row.validFrom, {
      columnLabel: "Valid from",
    }),
    validTo: createColumnConfig((row) => row.validTo, {
      columnLabel: "Valid to",
    }),
    route: createColumnConfig((row) => row.route, { columnLabel: "Route" }),
    sailingCategory: createColumnConfig((row) => row.sailingCategory, {
      columnLabel: "Sailing cat.",
    }),
    routeAgreementName: createColumnConfig((row) => row.routeAgreementName, {
      columnLabel: "Route agreement",
    }),
    vehicleType: createColumnConfig(
      (row) => resolveVehicleTypeName(row.vehicleType),
      { columnLabel: "Vehicle type" }
    ),
    length: createColumnConfig((row) => row.length, {
      columnLabel: "Length (m)",
    }),
    conversionCurrency: createColumnConfig(() => conversionCurrency ?? null, {
      columnLabel: "Display currency",
    }),
    convertedPrice: createColumnConfig((row) => row.convertedPrice, {
      columnLabel: "Display price",
    }),
    routeAgreementCurrency: createColumnConfig(
      (row) => row.routeAgreementCurrency,
      { columnLabel: "Agreement currency" }
    ),
    price: createColumnConfig((row) => row.price, {
      columnLabel: "Agreement price",
    }),
    weightFrom: createColumnConfig((row) => row.weightFrom, {
      columnLabel: "Weight from",
    }),
    weightTo: createColumnConfig((row) => row.weightTo, {
      columnLabel: "Weight to",
    }),
    unaccompanied: createColumnConfig(
      (row) => formatUnaccompanied(row.unaccompanied),
      { columnLabel: "Unacc" }
    ),
    customerNumber: createColumnConfig((row) => row.customerNumber, {
      columnLabel: "Customer no.",
    }),
    movementType: createColumnConfig((row) => row.movementType, {
      columnLabel: "Movement type",
    }),
    // An agreement with no vehicle type of its own covers every vehicle type.
    routeAgreementVehicleType: createColumnConfig(
      (row) =>
        row.routeAgreementVehicleType
          ? resolveVehicleTypeName(row.routeAgreementVehicleType)
          : "All",
      { columnLabel: "Route agreement vehicle type" }
    ),
  },
});
